import {
    useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { v7 as uuid } from 'uuid';
import { isEmpty } from 'lodash-es';
import { useFormValues, useSetFieldValue } from '@s20/components/forms/FormContext';
import { httpClient } from '@s20/services/httpClient';
import { useJobQueryKey } from '../common/useJobQueryKey.hook';
import { useResourceQueryKey } from '../common/useResourceQueryKey.hook';
import { ConfirmationStatus, DraggableResourceTypes, makeAssignmentItem } from '../../../dispatch/Dispatch';
import { useUpdateAssignment } from '../Jobs/Job/useUpdateAssignment.hook';
import { ApiRoutes } from '../../../Common';

const handleAssignmentUpdate = (resource, isMatch, selectedResources, allAssignments) => {
    // Get first selected resource that isn't already assigned
    const sel = selectedResources.find((asn) => resource.resourceId === asn.resourceId && !allAssignments.some((item) => item.assignment?.id === asn.resource.id));

    // `assignment` (not `jobAssignmentId`) is the true signal that the slot is actually taken.
    if (sel && !resource.assignment) {
        if (!sel.assignedEquipmentId) {
            const { resource: employee, jobId } = sel;

            return {
                ...resource,
                jobId,
                jobAssignmentId: uuid(),
                assignment: {
                    id: employee.id,
                    name: employee.employeeName,
                    confirmationStatusId: ConfirmationStatus.Scheduled,
                    confirmationStatusName: 'Scheduled',
                },
                employeeDispatchOffice: {
                    id: employee.dispatchCompanyId,
                    name: employee.dispatchCompanyName,
                },
            };
        }

        const { resource: equipment, jobId } = sel;
        const [requiredEquipment] = resource.requiredEquipment;

        return {
            ...resource,
            jobId,
            jobAssignmentId: uuid(),
            assignment: {
                id: equipment.id,
                identifier: equipment.equipmentName ?? equipment.identifier,
                description: equipment.equipmentName ?? equipment.identifier,
                confirmationStatusId: ConfirmationStatus.Scheduled,
                confirmationStatusName: 'Scheduled',
                equipmentTypeId: equipment.equipmentTypeId ?? requiredEquipment.id,
                employeeAssignedId: sel.equipmentEmployeeAssignmentId,
                equipmentAssignedId: sel.equipmentAssignedEquipmentId,
                dispatchLocation: {
                    id: equipment.dispatchCompanyId ?? sel.dispatchCompanyId,
                    name: equipment.dispatchCompanyName ?? sel.dispatchCompanyName,
                },
                equipmentType: {
                    id: equipment.equipmentTypeId ?? requiredEquipment.id,
                    description: equipment.equipmentTypeName,
                    isVehicle: equipment.isVehicle ?? requiredEquipment.isVehicle,
                    isAFAD: equipment.isAFAD,
                    isGeneric: resource.isGeneric,
                    requiredEquipment: sel.requiredEquipment ?? [],
                    groups: [],
                },
            },
        };
    }

    return resource;
};

export const useAssignResourcesButton = ({ setSelected, totalAvailable }) => {
    const {
        selectedResourceType,
        selectedResourceGroup,
        selectedJobId,
        selectedDate,
        replacementAssignmentId,
        replacementReason,
        replacementResourceType,
        replacingAssignmentId,
    } = useFormValues();

    const queryClient = useQueryClient();
    const jobQuery = useJobQueryKey();

    const { queryKey: resourceQueryKey } = useResourceQueryKey();

    const { queryKey } = jobQuery;
    const { data: jobData } = useQuery(jobQuery);

    const setFieldValue = useSetFieldValue();

    useEffect(() => {
        // If there are no more assignments needed, revert the resource selection
        if (!totalAvailable) {
            setFieldValue('selectedResourceGroup', null);
        }
    }, [totalAvailable, setFieldValue]);

    const resetReplacementState = useCallback(() => {
        setFieldValue('replacementAssignmentId', null);
        setFieldValue('replacementReason', null);
        setFieldValue('replacementResourceType', null);
        setFieldValue('replacementOriginalResourceId', null);
        setFieldValue('replacementLinkedAssignmentIds', null);
        setFieldValue('replacingAssignmentId', null);
    }, [setFieldValue]);

    const prevSelectedJobIdRef = useRef(selectedJobId);
    const prevSelectedDateRef = useRef(selectedDate);

    useEffect(() => {
        // The replace flow only ever runs against the specific (job, day) that was
        // expanded when it started. If selectedJobId OR selectedDate changes (collapsed,
        // a different job expanded, or the day switched) while replacement state is
        // still set, the flow was abandoned before an assignment was picked — clear it
        // so a later unrelated Assign (on this or any other job/day) doesn't get
        // silently redirected to replaceAssignment using a stale assignmentId.
        const jobChanged = prevSelectedJobIdRef.current !== selectedJobId;
        const dateChanged = prevSelectedDateRef.current !== selectedDate;

        prevSelectedJobIdRef.current = selectedJobId;
        prevSelectedDateRef.current = selectedDate;

        if (
            (jobChanged || dateChanged)
            && (replacementAssignmentId || replacementReason || replacementResourceType || replacingAssignmentId)
        ) {
            resetReplacementState();
        }
    }, [selectedJobId, selectedDate, replacementAssignmentId, replacementReason, replacementResourceType, replacingAssignmentId, resetReplacementState]);

    const day = moment(selectedDate).day();

    const totalRemainingAssignments = useMemo(() => {
        if (!selectedJobId) {
            return 0;
        }

        const job = jobData?.find((item) => item.id === selectedJobId || item.jobIdsInGroup?.includes(selectedJobId));
        const assignments = job?.assignments?.[day];

        if (!assignments) {
            return [];
        }

        // `assignment`, not `jobAssignmentId`, is the true signal that a slot is filled.
        return [
            ...assignments.staff.filter((staff) => !staff.assignment),
            ...assignments.equipment.filter((eq) => !eq.assignment),
        ];
    }, [jobData, selectedJobId, day]);

    const handleAssignmentUpdateMutations = useUpdateAssignment({
        jobId: selectedJobId,
        onMutate: handleAssignmentUpdate,
    });

    const packageAssignmentData = useCallback((sel) => {
        let trackedRemaining = [...totalRemainingAssignments];
        const items = [];

        // For grouped jobs, each slot may have its own jobId
        // We want to make sure that we use the actual jobId for all operations, rather than the parent
        sel.forEach((resource) => {
            const baseItem = makeAssignmentItem({
                day,
                resource,
                assignmentDate: selectedDate,
                weekOfDate: selectedDate,
                jobId: selectedResourceGroup.jobId,
                resourceId: selectedResourceGroup.resourceId,
                employeeId: selectedResourceType === DraggableResourceTypes.Staff ? resource.id : undefined,
                equipmentId: selectedResourceType === DraggableResourceTypes.Equipment ? resource.id : undefined,
                isRentalPickup: selectedResourceGroup.isRentalPickup,
                dispatchCompanyId: resource.dispatchCompanyId,
                dispatchCompanyName: resource.dispatchCompanyName,
            });

            items.push(baseItem);

            if (!isEmpty(resource.equipment)) {
                resource.equipment.forEach((eq) => {
                    const equipmentAssignmentIndex = trackedRemaining.findIndex((asn) => asn.requiredEquipment?.some((re) => re.id === eq.equipmentTypeId));

                    if (equipmentAssignmentIndex !== -1) {
                        const equipmentAssignment = { ...trackedRemaining[equipmentAssignmentIndex] };
                        trackedRemaining = trackedRemaining.filter((_, index) => index !== equipmentAssignmentIndex);

                        let additionalInfo = {};

                        if (selectedResourceType === DraggableResourceTypes.Staff && eq.linkedEquipmentTypeId) {
                            additionalInfo = {
                                ...additionalInfo,
                                requiredEquipment: [{ id: eq.linkedEquipmentTypeId }],
                                equipmentAssignedEquipmentId: eq.linkedEquipmentId,
                            };
                        }

                        const equipmentItem = makeAssignmentItem({
                            ...baseItem,
                            resource: eq,
                            resourceId: equipmentAssignment.resourceId,
                            equipmentId: eq.id,
                            assignedEmployeeId: null,
                            equipmentEmployeeAssignmentId: selectedResourceType === DraggableResourceTypes.Staff ? resource.id : undefined,
                            equipmentAssignedEquipmentId: selectedResourceType === DraggableResourceTypes.Equipment ? resource.id : undefined,
                            isRentalPickup: equipmentAssignment.isRentalPickup,
                            ...additionalInfo,
                        });

                        items.push(equipmentItem);
                    }
                });
            }
        });

        return items;
    }, [day, selectedDate, selectedResourceGroup, totalRemainingAssignments, selectedResourceType]);

    const handleAssignmentButtonClick = useMutation({
        mutationFn: async (items) => {
            if (isEmpty(items)) {
                return true;
            }

            // Check if we're in replace mode
            const isReplaceMode = !!replacementAssignmentId && !!replacementReason;

            if (isReplaceMode) {
                // Replace the resource on its own record (keeps the audit history in place)...
                const [firstItem, ...linkedItems] = items;
                const newEmployeeId = selectedResourceType === DraggableResourceTypes.Staff ? firstItem.assignedEmployeeId : null;
                const newEquipmentId = selectedResourceType === DraggableResourceTypes.Equipment ? firstItem.assignedEquipmentId : null;

                const replaceModel = {
                    assignmentId: replacementAssignmentId,
                    newEmployeeId,
                    newEquipmentId,
                    replacementReason,
                };

                const replaceResult = await httpClient.andGetResponse(
                    httpClient.types.post,
                    ApiRoutes.dispatch.replaceAssignment(),
                    replaceModel,
                    'Server Error Replacing Assignment',
                    null,
                    true,
                );

                // ...then assign any equipment the chosen resource brings along (e.g. the new
                // staff's work truck) to its open slot, exactly as a normal assignment would.
                if (replaceResult && !isEmpty(linkedItems)) {
                    await httpClient.andGetResponse(
                        httpClient.types.post,
                        ApiRoutes.dispatch.saveAssignment(),
                        { weekOfDate: new Date(), assignments: linkedItems },
                        'Server Error Saving Job Assignment',
                        null,
                        true,
                    );
                }

                return replaceResult;
            }

            // Normal assignment flow
            const payload = { weekOfDate: new Date(), assignments: items };

            return httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.dispatch.saveAssignment(),
                payload,
                'Server Error Saving Job Assignment',
                null,
                true,
            );
        },
        onMutate: (sel) => {
            const prev = queryClient.getQueryData(resourceQueryKey);

            queryClient.setQueryData(resourceQueryKey, (oldData) => {
                if (oldData) {
                    return oldData.filter((resource) => !sel.some((item) => item.resource.id === resource.id));
                }

                return oldData;
            });

            const previousJobData = handleAssignmentUpdateMutations.onMutate(sel);

            return { previousData: prev, previousJobData };
        },
        onError: (e, _, context) => {
            console.log('error', { e });

            if (context?.previousData) {
                queryClient.setQueryData(resourceQueryKey, context.previousData);
            }

            if (context?.previousJobData) {
                queryClient.setQueryData(queryKey, context.previousJobData);
            }

            // Restore the collapsed slot so a failed replace doesn't hide the assignment,
            // and clear all replace-mode state so a later unrelated Assign doesn't get
            // silently redirected to replaceAssignment using this stale assignmentId.
            if (replacementAssignmentId || replacingAssignmentId) {
                resetReplacementState();
            }
        },
        onSuccess: async (e) => {
            console.log('success', { e });

            setSelected([]);

            // Clear replace mode if successful
            if (replacementAssignmentId) {
                // Wait for the job list to refetch the persisted replacement before revealing
                // the slot, otherwise the pre-replacement resource flashes for a moment (the
                // raw cache still holds it until the refetch lands).
                await queryClient.invalidateQueries({ queryKey });
                queryClient.invalidateQueries({ queryKey: resourceQueryKey });

                resetReplacementState();
                setFieldValue('selectedResourceGroup', null);

                return;
            }

            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: resourceQueryKey });
        },
    });

    return {
        handleAssignmentButtonClick,
        packageAssignmentData,
        resetReplacementState,
    };
};